import supportAPI from '../_apis/supportAPI';
import {convertDateToTimestamp, formatCurrency} from './formattingUtils';
import {STEP_VALUE} from '../system/defaults';
import {addActivity} from './activityUtilities';
import {addNotification} from './notificationUtilities';
import {sendNote} from './contactUtilities';
import {calculateTicketCost} from './ticketUtils';
import {gaEvent} from '../system/ga';

export const TICKET_STATUS = Object.freeze({
    BACKLOG: '1', ASSIGNED: '2', REVIEW: '4', COMPLETED: '5',
    CANCELLED: '6', NEEDS_INFO: '7', READY_TO_LAUNCH: '8'
});

export const normalizeTicket = ticket => {
    const normalized = {...ticket};
    ['ticket_id', 'contact_id', 'company_id', 'type_id', 'priority_id', 'status_id',
        'assignee_id', 'estimate_required', 'estimated_time', 'estimate_approved', 'on_hold']
        .forEach(field => {
            normalized[field] = ticket[field] == null || ticket[field] === 'null'
                ? null : String(ticket[field]);
        });
    return normalized;
};

export const buildTicketUpdate = (ticket, field, value) => {
    const update = {
        contact_id: ticket.contact_id, company_id: ticket.company_id,
        title: ticket.title, description: ticket.description ?? null,
        [field]: value
    };
    if (field === 'assignee_id') {
        update.assignee_id = value == null || value === 'null' ? null : String(value);
        if (!update.assignee_id) update.status_id = TICKET_STATUS.BACKLOG;
        else if (String(ticket.status_id) === TICKET_STATUS.BACKLOG) update.status_id = TICKET_STATUS.ASSIGNED;
    }
    const nextStatus = field === 'status_id' ? String(value) : update.status_id;
    if (nextStatus === TICKET_STATUS.ASSIGNED) {
        const assignee = update.assignee_id ?? ticket.assignee_id;
        if (!assignee || assignee === 'null' || Number(assignee) === 0) update.assignee_id = '1';
        update.date_assigned = convertDateToTimestamp(new Date());
    }
    if (nextStatus === TICKET_STATUS.COMPLETED) update.date_completed = convertDateToTimestamp(new Date());
    if (field === 'estimated_time') update.estimate_approved = '-1';
    if (field === 'estimate_approved') update.estimate_approved = '1';
    if (field === 'on_hold') update.on_hold = String(value);
    if (field === 'date_due') update.date_due = value == null ? null : String(value);
    return update;
};

export const saveTicketField = async (ticket, field, value) => {
    const update = buildTicketUpdate(ticket, field, value);
    const response = await supportAPI.put(
        `/ticket/${ticket.ticket_id}`, JSON.stringify(update)
    );
    if (!response.data) throw new Error('Ticket not updated.');
    return normalizeTicket({...ticket, ...update, ...response.data});
};

// Run after the save: notification or analytics failures must not undo a successful update.
export const recordTicketUpdate = async ({ticket, field, value, contact, types, priorities, statuses, agentList}) => {
    const findDefault = items => items.find(item => String(item.id) === String(value));
    const prefix = `Ticket #${ticket.ticket_id}`;
    let activity = '';
    let notification = '';
    let assignedAgent = null;
    switch (field) {
        case 'type_id': {
            const type = findDefault(types);
            activity = `Type changed${type ? ` to ${type.name}` : ''}.`;
            break;
        }
        case 'priority_id': {
            const priority = findDefault(priorities);
            activity = `Priority changed${priority ? ` to ${priority.name}` : ''}.`;
            notification = `${prefix} ${activity.charAt(0).toLowerCase()}${activity.slice(1)}`;
            break;
        }
        case 'status_id': {
            const status = findDefault(statuses);
            activity = `Status changed${status ? ` to ${status.name}` : ''}.`;
            notification = `${prefix} ${activity.charAt(0).toLowerCase()}${activity.slice(1)}`;
            break;
        }
        case 'date_due':
            activity = value == null ? 'Due date cleared.' : `Due date changed to ${value}.`;
            notification = `${prefix} ${activity.charAt(0).toLowerCase()}${activity.slice(1)}`;
            break;
        case 'assignee_id':
            if (ticket.assignee_id) {
                assignedAgent = agentList.find(agent => String(agent.contact_id) === ticket.assignee_id);
                const name = assignedAgent ? `${assignedAgent.first_name} ${assignedAgent.last_name}` : 'an agent';
                activity = `Assigned ${name}.`;
                notification = `${prefix} assigned to ${name}.`;
            } else {
                activity = 'Ticket unassigned and status changed to Backlog.';
                notification = `${prefix} unassigned and status changed to Backlog.`;
            }
            break;
        case 'estimated_time': {
            const cost = formatCurrency(Number(value) * STEP_VALUE);
            activity = `Estimate updated to ${cost}.`;
            notification = `${prefix} estimate changed to ${cost}. APPROVAL REQUIRED`;
            break;
        }
        case 'estimate_approved':
            activity = 'Estimate approved.';
            notification = `${prefix} estimate has been approved.`;
            break;
        default:
    }

    const jobs = [];
    if (activity) jobs.push(addActivity({contact_id: contact.contact_id, ticket_id: ticket.ticket_id, note: activity}));
    if (assignedAgent) {
        jobs.push(addNotification({
            contact_id: assignedAgent.contact_id, ticket_id: ticket.ticket_id,
            note: `You have been assigned to ticket ${ticket.ticket_id}.`
        }));
        jobs.push(sendNote(assignedAgent.contact_id, 'User X Support: Assigned Task',
            `You have been assigned to ticket ${ticket.ticket_id}.`));
    }
    if (notification) {
        const recipients = new Set([ticket.contact_id, ticket.assignee_id].filter(id =>
            id && id !== String(contact.contact_id) && id !== String(assignedAgent?.contact_id)
        ));
        recipients.forEach(contact_id => jobs.push(addNotification({
            contact_id, ticket_id: ticket.ticket_id, note: notification
        })));
    }
    jobs.push((async () => {
        const cost = await calculateTicketCost(ticket, true);
        let action = 'ticket_updated';
        if (field === 'status_id' && String(value) === TICKET_STATUS.COMPLETED) action = 'ticket_completed';
        if (field === 'status_id' && String(value) === TICKET_STATUS.CANCELLED) action = 'ticket_cancelled';
        if (field === 'estimated_time') action = 'estimate_updated';
        gaEvent({action, category: 'Tickets', label: prefix, value: cost, currency: 'USD',
            company_id: ticket.company_id, ticket_id: ticket.ticket_id});
    })());
    const results = await Promise.allSettled(jobs);
    if (results.some(result => result.status === 'rejected')) {
        throw new Error('Ticket saved, but some activity, notification, or analytics updates failed.');
    }
};
